/**
 * ContentForge — Mission Control Dashboard View
 *
 * The main dashboard shell with sub-navigation, stats, agent cards,
 * pipeline visualization, and activity feed.
 */

import { html, nothing, type TemplateResult } from "lit";
import type {
  ContentForgeDashboardData,
  ContentForgeSubView,
  ContentForgeAgentState,
  ContentForgeActivityEntry,
  CfPinterestSite,
} from "../controllers/contentforge.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import { renderSettingsView } from "./contentforge-settings.ts";

// ─── Agent Profile Popup State ──────────────────────────────────────────

let _profileAgentKey: string | null = null;
let _profilePromptValue = "";
let _profileDefaultPrompt = "";
let _profileSaving = false;
let _profileSaved = false;
let _profileLoading = false;
let _profileClient: GatewayBrowserClient | null = null;
let _profileOnRefresh: (() => void) | null = null;
let _profileSiteId: string | null = null;

function openAgentProfile(
  agentKey: string,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
  activeSiteId?: string | null,
) {
  _profileAgentKey = agentKey;
  _profileSaving = false;
  _profileClient = client;
  _profileOnRefresh = onRefresh;
  _profileSiteId = activeSiteId ?? null;
  _profileLoading = true;
  onRefresh();

  // Load defaults + current prompt
  if (client) {
    void (async () => {
      try {
        const defaults = await client.request<Record<string, string>>(
          "contentforge.sites.getDefaults",
          {},
        );
        const promptKey = `${agentKey}Prompt`;
        _profileDefaultPrompt = defaults?.[promptKey] ?? "";

        // Load site-specific prompt using activeSiteId
        let siteId = _profileSiteId;
        if (!siteId) {
          // Fallback: find first site
          const sitesRes = await client.request<{
            sites: Array<{ id: string }>;
          }>("contentforge.sites.list", {});
          siteId = sitesRes?.sites?.[0]?.id ?? null;
        }
        if (siteId) {
          const site = await client.request<Record<string, unknown>>("contentforge.sites.get", {
            id: siteId,
          });
          _profilePromptValue = (site?.[promptKey] as string) || _profileDefaultPrompt;
        } else {
          _profilePromptValue = _profileDefaultPrompt;
        }
      } catch {
        _profilePromptValue = _profileDefaultPrompt;
      }
      _profileLoading = false;
      onRefresh();
    })();
  }
}

function closeAgentProfile() {
  _profileAgentKey = null;
  _profileOnRefresh?.();
}

async function saveAgentPrompt() {
  if (!_profileClient || !_profileAgentKey) {
    alert("Gateway not connected. Please reconnect and try again.");
    return;
  }
  _profileSaving = true;
  _profileOnRefresh?.();
  try {
    // Use activeSiteId or fallback to first site
    let siteId = _profileSiteId;
    if (!siteId) {
      const sitesRes = await _profileClient.request<{ sites: Array<{ id: string }> }>(
        "contentforge.sites.list",
        {},
      );
      siteId = sitesRes?.sites?.[0]?.id ?? null;
    }
    if (!siteId) {
      alert("No site configured. Please create a site first in Settings.");
      _profileSaving = false;
      _profileOnRefresh?.();
      return;
    }
    const textarea = document.getElementById("cf-profile-prompt") as HTMLTextAreaElement | null;
    const value = textarea?.value ?? _profilePromptValue;
    await _profileClient.request("contentforge.sites.update", {
      id: siteId,
      patch: { [`${_profileAgentKey}Prompt`]: value || undefined },
    });
    _profilePromptValue = value;
    _profileSaving = false;
    _profileSaved = true;
    _profileOnRefresh?.();
    setTimeout(() => {
      _profileSaved = false;
      _profileOnRefresh?.();
    }, 2000);
    return;
  } catch (err) {
    console.error("[CF] Failed to save agent prompt:", err);
    alert("Failed to save prompt. Check gateway connection.");
  }
  _profileSaving = false;
  _profileOnRefresh?.();
}

function resetAgentPrompt() {
  const textarea = document.getElementById("cf-profile-prompt") as HTMLTextAreaElement | null;
  if (textarea) {
    textarea.value = _profileDefaultPrompt;
  }
  _profilePromptValue = _profileDefaultPrompt;
}

// ─── Types ──────────────────────────────────────────────────────────────

export type ContentForgeProps = {
  loading: boolean;
  error: string | null;
  dashboard: ContentForgeDashboardData | null;
  subView: ContentForgeSubView;
  onSubViewChange: (view: ContentForgeSubView) => void;
  onRefresh: () => void;
  client?: GatewayBrowserClient | null;
  activeSiteId?: string | null;
  createMode?: boolean;
  onProjectCreated?: (siteId: string) => void;
};

// ─── Agent Meta ─────────────────────────────────────────────────────────

const AGENT_META: Record<
  string,
  {
    emoji: string;
    label: string;
    color: string;
    role?: string;
    desc?: string;
    skills?: string[];
    avatar?: string;
  }
> = {
  arthur: {
    emoji: "🎩",
    label: "Arthur",
    color: "var(--cf-accent)",
    role: "Office Manager",
    desc: "Coordinates, validates, keeps the office running. Your right-hand man.",
    skills: ["Quality", "Telegram", "Cost Tracking"],
    avatar: "/agents/ARTHUR.webp",
  },
  strategist: {
    emoji: "🧠",
    label: "Strategist",
    color: "var(--cf-agent-strategist)",
    role: "Outline Architect",
    desc: "Plans article structure, H2 sections, meta descriptions, and content strategy.",
    skills: ["Outlines", "SEO", "Strategy"],
    avatar: "/agents/STRATEGIST.webp",
  },
  sourcer: {
    emoji: "🔍",
    label: "Sourcer",
    color: "var(--cf-agent-sourcer)",
    role: "Product Hunter",
    desc: "Finds matching products from your CSV catalog using semantic search.",
    skills: ["Search", "Products", "Matching"],
    avatar: "/agents/SOURCER.webp",
  },
  writer: {
    emoji: "✍️",
    label: "Writer",
    color: "var(--cf-agent-writer)",
    role: "Content Writer",
    desc: "Writes full SEO articles with product integration and FAQ sections.",
    skills: ["Write", "Quality", "Design"],
    avatar: "/agents/WRITER.webp",
  },
  linker: {
    emoji: "🔗",
    label: "Linker",
    color: "#60a5fa",
    role: "Link Specialist",
    desc: "Injects internal links naturally from your sitemap + authoritative sources.",
    skills: ["Links", "SEO", "Sitemap"],
    avatar: "/agents/LINKER.webp",
  },
  artist: {
    emoji: "🎨",
    label: "Artist",
    color: "var(--cf-agent-artist)",
    role: "Visual Designer",
    desc: "Generates featured images via DALL-E 3 for each article.",
    skills: ["DALL-E", "Images"],
    avatar: "/agents/ARTIST.webp",
  },
  publisher: {
    emoji: "🚀",
    label: "Publisher",
    color: "var(--cf-agent-publisher)",
    role: "WordPress Publisher",
    desc: "Pushes articles to WordPress with Rank Math SEO, categories, and images.",
    skills: ["WP", "Rank Math", "Deploy"],
    avatar: "/agents/PUBLISHER.webp",
  },
  auditor: {
    emoji: "🔧",
    label: "Auditor",
    color: "var(--cf-agent-auditor)",
    role: "Quality Auditor",
    desc: "Scans existing articles for broken links, images, and out-of-stock products.",
    skills: ["Audit", "Fix", "Monitor"],
    avatar: "/agents/AUDITOR.webp",
  },
  miner: {
    emoji: "⛏️",
    label: "Miner",
    color: "var(--cf-agent-miner)",
    role: "Keyword Miner",
    desc: "Extracts new keyword ideas from product names, descriptions, and trends.",
    skills: ["Keywords", "Research"],
    avatar: "/agents/MINER.webp",
  },
};

const SUB_VIEWS: { id: ContentForgeSubView; label: string }[] = [
  { id: "dashboard", label: "Dashboard" },
  { id: "tasks", label: "Tasks" },
  { id: "content", label: "Content" },
  { id: "approvals", label: "Approvals" },
  { id: "calendar", label: "Calendar" },
  { id: "projects", label: "Projects" },
  { id: "memory", label: "Memory" },
  { id: "office", label: "Office" },
  { id: "team", label: "Team" },
  { id: "settings", label: "Settings" },
];

const PIPELINE_STAGES: { key: string; label: string }[] = [
  { key: "ideas", label: "Ideas" },
  { key: "outline", label: "Outline" },
  { key: "sourcing", label: "Sourcing" },
  { key: "writing", label: "Writing" },
  { key: "media", label: "Media" },
  { key: "review", label: "Review" },
  { key: "scheduled", label: "Scheduled" },
  { key: "published", label: "Published" },
];

// ─── Helpers ────────────────────────────────────────────────────────────

function formatTime(timestamp: number): string {
  const d = new Date(timestamp);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function statusColor(status: string): string {
  switch (status) {
    case "success":
      return "cf-activity__badge--success";
    case "warning":
      return "cf-activity__badge--warning";
    case "error":
      return "cf-activity__badge--error";
    default:
      return "cf-activity__badge--info";
  }
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderContentForge(props: ContentForgeProps) {
  // Team view renders independently — no sub-nav tabs
  if (props.subView === "team") {
    return html`
      <div class="cf-shell">
        ${renderTeamView(props.dashboard, props.client ?? null, props.onRefresh, props.activeSiteId)}
      </div>
    `;
  }

  // Settings view renders independently
  if (props.subView === "settings") {
    return html`
      <div class="cf-shell">
        ${renderSettingsView(
          props.dashboard,
          props.client ?? null,
          props.onRefresh,
          props.activeSiteId,
          props.createMode,
          props.onProjectCreated,
        )}
      </div>
    `;
  }

  return html`
    <div class="cf-shell">
      ${renderSubNav(props)}
      ${
        props.loading && !props.dashboard
          ? html`
              <div class="cf-loading"><span class="cf-spinner"></span> Loading Mission Control...</div>
            `
          : nothing
      }
      ${
        props.error
          ? html`<div class="cf-section" style="border-color: var(--cf-danger);">
            <div class="cf-section__title" style="color: var(--cf-danger)">Error</div>
            <div style="color: var(--cf-text-secondary); font-size: 13px; margin-top: 8px;">${props.error}</div>
          </div>`
          : nothing
      }
      ${!props.loading && !props.dashboard && !props.error ? renderEmptyState(props) : nothing}
      ${
        props.dashboard && props.subView === "dashboard"
          ? renderDashboardView(props.dashboard, props.onRefresh, props.client ?? null)
          : nothing
      }
      ${
        props.dashboard && props.subView !== "dashboard"
          ? renderPlaceholderView(props.subView)
          : nothing
      }
    </div>
  `;
}

// ─── Sub-Navigation ─────────────────────────────────────────────────────

function renderSubNav(props: ContentForgeProps) {
  const pendingCount = props.dashboard?.stats?.pendingApprovals ?? 0;
  return html`
    <nav class="cf-subnav" role="tablist">
      ${SUB_VIEWS.map(
        (v) => html`
          <button
            class="cf-subnav__item ${props.subView === v.id ? "cf-subnav__item--active" : ""}"
            role="tab"
            aria-selected=${props.subView === v.id}
            @click=${() => props.onSubViewChange(v.id)}
          >
            ${v.label}${
              v.id === "approvals" && pendingCount > 0
                ? html`<span class="cf-subnav__badge">${pendingCount}</span>`
                : nothing
            }
          </button>
        `,
      )}
    </nav>
  `;
}

// ─── Dashboard View ─────────────────────────────────────────────────────

function renderDashboardView(
  data: ContentForgeDashboardData,
  onRefresh: () => void,
  client: GatewayBrowserClient | null = null,
) {
  return html`
    ${renderLiveBar(data)}
    ${renderStatsBar(data)}
    ${renderPipeline(data)}
    <div class="cf-grid">
      <div>
        ${renderAgentStatus(data)}
      </div>
      <div>
        ${renderActivityFeed(data, onRefresh)}
      </div>
    </div>
    ${(data.pinterest ?? []).map((site) => renderPinterestPanel(site, client, onRefresh))}
  `;
}

// ─── Pinterest Panel ─────────────────────────────────────────────────────

function fmtAgo(ms: number | null): string {
  if (!ms) {
    return "never";
  }
  const h = Math.round((Date.now() - ms) / 3600_000);
  return h < 1 ? "<1h ago" : h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

function pinStatusChip(status: string): TemplateResult {
  const color = status === "published" ? "#7C9A72" : status === "pending" ? "#b8860b" : "#b4462f";
  return html`<span
    style="display:inline-block;padding:1px 8px;border-radius:999px;font-size:11px;font-weight:600;background:${color}22;color:${color}"
    >${status}</span
  >`;
}

// Lazy thumbnail cache: fetched once per pin via contentforge.pins.thumbnail
const _pinThumbs = new Map<string, string>();
const _pinThumbsInflight = new Set<string>();

function pinThumb(
  pinId: string,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
): TemplateResult {
  const cached = _pinThumbs.get(pinId);
  if (cached) {
    return html`<img
      src=${cached}
      style="width:40px;height:60px;object-fit:cover;border-radius:6px;display:block"
    />`;
  }
  if (client && !_pinThumbsInflight.has(pinId)) {
    _pinThumbsInflight.add(pinId);
    void client
      .request<{ thumb: string }>("contentforge.pins.thumbnail", { pinId })
      .then((res) => {
        if (res?.thumb) {
          _pinThumbs.set(pinId, res.thumb);
          onRefresh();
        }
      })
      .catch(() => {})
      .finally(() => _pinThumbsInflight.delete(pinId));
  }
  return html`
    <div
      style="width: 40px; height: 60px; border-radius: 6px; background: rgba(128, 128, 128, 0.15)"
    ></div>
  `;
}

function fmtEta(ms: number | null): string {
  if (!ms) {
    return "—";
  }
  const d = new Date(ms);
  return `~${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

// ── Template previews (thumbnails per template, lazy-loaded per site) ────────
type TemplatePreview = { template: string; thumb: string; source: string };
const _tplPreviews = new Map<string, TemplatePreview[] | "loading">();
let _tplZoom: TemplatePreview | null = null;

async function loadTemplatePreviews(
  siteId: string,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
): Promise<void> {
  if (!client || _tplPreviews.get(siteId)) {
    return;
  }
  _tplPreviews.set(siteId, "loading");
  onRefresh();
  try {
    const res = await client.request<{ previews: TemplatePreview[] }>(
      "contentforge.pins.templatePreviews",
      { siteId },
    );
    _tplPreviews.set(siteId, res?.previews ?? []);
  } catch {
    _tplPreviews.delete(siteId);
  }
  onRefresh();
}

function renderTemplatePreviews(
  site: CfPinterestSite,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
): TemplateResult {
  const entry = _tplPreviews.get(site.siteId);
  if (!entry) {
    return html`<button
      style="font-size:12px;padding:3px 10px;border-radius:999px;background:transparent;border:1px solid rgba(128,128,128,.35);cursor:pointer;opacity:.8"
      @click=${() => void loadTemplatePreviews(site.siteId, client, onRefresh)}
    >
      🖼 Preview templates
    </button>`;
  }
  if (entry === "loading") {
    return html`
      <span style="font-size: 12px; opacity: 0.6">rendering previews…</span>
    `;
  }
  return html`
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px">
      ${entry.map(
        (pv) => html`
          <div
            style="cursor:zoom-in;text-align:center"
            title="${pv.source === "live" ? "Latest live pin" : "Rendered sample"} — click to zoom"
            @click=${() => {
              _tplZoom = pv;
              onRefresh();
            }}
          >
            <img
              src=${pv.thumb}
              style="width:96px;height:144px;object-fit:cover;border-radius:8px;border:1px solid rgba(128,128,128,.3);display:block"
            />
            <div style="font-size:10.5px;opacity:.65;margin-top:2px">
              ${pv.template}${pv.source === "sample" ? " *" : ""}
            </div>
          </div>
        `,
      )}
    </div>
    ${
      entry.some((pv) => pv.source === "sample")
        ? html`
            <div style="font-size: 10.5px; opacity: 0.45; margin-top: 4px">
              * rendered sample — no live pin with this template yet
            </div>
          `
        : nothing
    }
    ${
      _tplZoom
        ? html`
          <div
            style="position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,.75);display:flex;align-items:center;justify-content:center;cursor:zoom-out"
            @click=${() => {
              _tplZoom = null;
              onRefresh();
            }}
          >
            <img
              src=${_tplZoom.thumb}
              style="max-height:92vh;max-width:92vw;border-radius:12px;box-shadow:0 12px 60px rgba(0,0,0,.5)"
            />
          </div>
        `
        : nothing
    }
  `;
}

export function renderPinterestPanel(
  site: CfPinterestSite,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
) {
  const num = (n: number | null) => (n === null ? "—" : String(n));
  const th = `text-align:left;padding:6px 10px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;opacity:.65;position:sticky;top:0`;
  const td = `padding:6px 10px;font-size:13px;border-top:1px solid rgba(128,128,128,.15);vertical-align:middle`;
  const s = site.strategist;

  const retryPin = async (pinId: string) => {
    if (!client) {
      return;
    }
    try {
      await client.request("contentforge.pins.retry", { pinId });
      onRefresh();
    } catch (err) {
      console.error("pin retry failed", err);
    }
  };

  const replacePin = async (pinId: string) => {
    if (!client) {
      return;
    }
    try {
      await client.request("contentforge.pins.delete", { pinId, regenerate: true });
      _pinThumbs.delete(pinId);
      onRefresh();
    } catch (err) {
      console.error("pin replace failed", err);
    }
  };

  const tile = (label: string, value: string | number, sub = "") => html`
    <div style="flex:1;min-width:120px;padding:10px 14px;border-radius:10px;background:rgba(128,128,128,.08)">
      <div style="font-size:22px;font-weight:700;line-height:1.1">${value}</div>
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:.05em;opacity:.6;margin-top:2px">
        ${label}${sub ? html` <span style="text-transform:none;letter-spacing:0">· ${sub}</span>` : nothing}
      </div>
    </div>`;

  return html`
    <div class="cf-card" style="margin-top:16px;padding:16px;border-radius:12px;border:1px solid rgba(128,128,128,.2)">
      <div style="display:flex;flex-wrap:wrap;align-items:baseline;gap:12px;margin-bottom:12px">
        <strong style="font-size:15px">📌 Pinterest — ${site.siteName}</strong>
        <span style="font-size:12.5px;opacity:.8">
          window ${site.activeHours[0]}–${site.activeHours[1]}h UTC
        </span>
        <span style="font-size:11.5px;opacity:.55;margin-left:auto">
          analytics ${fmtAgo(site.lastAnalyticsPullAt)} · reflection ${fmtAgo(site.lastReflectionAt)}
        </span>
      </div>

      <!-- How this system works — the short bible, always at hand -->
      <details style="margin-bottom:12px;font-size:12.5px;border:1px dashed rgba(128,128,128,.3);border-radius:10px;padding:8px 12px">
        <summary style="cursor:pointer;font-weight:600;opacity:.8">ℹ️ How this system works</summary>
        <div style="line-height:1.65;opacity:.85;margin-top:8px;max-width:860px">
          <b>Create.</b> Every published article gets ${site.variantsPerArticle ?? 3} pins on up to 3 boards
          across different pillars (Splinter Method: primary topic + situation + adjacent), each with a fresh
          image and a DIFFERENT title angle, plus one <i>product-hero</i> pin — the article's #1 Amazon product
          in a generated scene, deep-linked to its card. The blog category (GPT-picked) drives board matching,
          so blog taxonomy and board taxonomy are one decision.
          <br /><b>Post.</b> The drip posts at most 1 pin per tick, ≤${site.dailyPinCap}/day, only inside the
          active window, evenly spaced with jitter; one article gets a new pin at most every 3 days
          (anti-spam for young accounts). New boards are created lazily, max 2/day. Oldest article first.
          <br /><b>Measure.</b> Analytics pull daily for pins older than 48h (fresh pins report late — totals
          lag Pinterest by ~2 days). Weekly (~6.5d) the <b>PinStrategist</b> reflects: retunes template weights
          from clicks-per-impression (needs 12+ measured pins and 500+ impressions per template; can demote a
          template to weight 1 but never remove it), ramps dailyPinCap +1/week toward 12 while the account is
          14+ days old with zero failures and non-declining impressions (-1 on 3+ failures/7d), raises
          variants to 4 once cap ≥8 and pins average ≥1 outbound click, sends a fresh 2-pin <b>wave</b> to the
          top article with 3+ clicks, and turns winners into 4 new adjacent keywords for the Miner.
          <br /><b>Self-activating:</b> more learnings are built in but stay silent until the data
          earns them — product-hero look arbitration (500+ impr per look), title-angle insights fed
          back into copy (500+ impr per angle), a preferred posting window (300+ impr per third), a
          performance tiebreaker in board matching (500+ impr per board), and true account-level
          analytics the moment the token gains the user_accounts:read scope.
          <br /><b>North star:</b> outbound clicks. Saves feed reach; impressions are exhaust.
        </div>
      </details>

      <!-- Stats strip + evolution -->
      <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:stretch;margin-bottom:14px">
        ${tile("Posted today", `${site.postedToday}/${site.dailyPinCap}`)}
        ${tile("Live pins", site.publishedTotal)}
        ${tile("In queue", site.pendingTotal, site.failedTotal > 0 ? `${site.failedTotal} failed` : "")}
        ${tile("Outbound clicks", s?.totals.clicks ?? 0, "the money metric")}
        ${tile(
          "Impressions",
          s?.totals.impressions ?? 0,
          `${s?.totals.saves ?? 0} saves${s?.totals.source === "account" ? " · account-level" : ""}`,
        )}
        ${renderEvolution(
          (s?.accountDaily?.length ?? 0) >= 2 ? (s?.accountDaily ?? []) : (s?.analyticsDaily ?? []),
        )}
      </div>

      <!-- Template mix + PinStrategist changes -->
      <div style="margin-bottom:14px">
        <div style="font-size:12px;font-weight:700;opacity:.75;margin-bottom:6px">TEMPLATE MIX</div>
        <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
          ${Object.entries(site.templateWeights ?? {}).map(
            ([t, w]) => html`<span
              style="font-size:12px;padding:3px 10px;border-radius:999px;background:rgba(124,154,114,.15);border:1px solid rgba(124,154,114,.3)"
              >${t} <b>${w}</b></span
            >`,
          )}
          ${renderTemplatePreviews(site, client, onRefresh)}
        </div>
        ${
          (s?.weightsHistory ?? []).length > 0
            ? html`<div style="font-size:12px;opacity:.7;margin-top:6px">
              ${(s?.weightsHistory ?? []).toReversed().map((h) => {
                const diff = Object.keys(h.after)
                  .filter((t) => h.before[t] !== h.after[t])
                  .map((t) => `${t} ${h.before[t] ?? "·"}→${h.after[t]}`)
                  .join(", ");
                return html`<div>
                  🧠 ${new Date(h.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}:
                  ${diff || "no change"}
                </div>`;
              })}
            </div>`
            : html`<div style="font-size:12px;opacity:.5;margin-top:6px">
              No weight changes yet — PinStrategist retunes weekly once ${s?.minPinsForRetune ?? 12}+ pins are measured.
            </div>`
        }
      </div>

      <!-- Boards -->
      <div style="margin-bottom:14px">
        <div style="font-size:12px;font-weight:700;margin-bottom:6px;opacity:.75">
          BOARDS (${site.boards.filter((b) => b.created).length}/${site.boards.length} on Pinterest)
        </div>
        <div style="overflow-x:auto;max-height:260px;overflow-y:auto">
          <table style="width:100%;border-collapse:collapse;min-width:560px">
            <thead><tr>
              <th style=${th}>Board</th><th style=${th}>Pillar</th><th style=${th}>On PT</th>
              <th style=${th}>Live</th><th style=${th}>Queue</th>
              <th style=${th}>Clicks</th><th style=${th}>Impr</th><th style=${th}>Saves</th>
            </tr></thead>
            <tbody>
              ${site.boards.map(
                (b) => html`<tr>
                  <td style=${td}>${b.name}</td>
                  <td style="${td};font-size:11px;opacity:.65">${b.pillar}</td>
                  <td style=${td}>${b.created ? "✓" : "…"}</td>
                  <td style=${td}>${b.published}</td>
                  <td style=${td}>${b.pending}</td>
                  <td style=${td}><b>${b.clicks}</b></td>
                  <td style=${td}>${b.impressions}</td>
                  <td style=${td}>${b.saves}</td>
                </tr>`,
              )}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pin queue -->
      <div style="margin-bottom:14px">
        <div style="font-size:12px;font-weight:700;margin-bottom:6px;opacity:.75">PIN QUEUE &amp; RECENT</div>
        <div style="overflow-x:auto;max-height:420px;overflow-y:auto">
          <table style="width:100%;border-collapse:collapse;min-width:760px">
            <thead><tr>
              <th style=${th}></th><th style=${th}>Pin</th><th style=${th}>Template</th>
              <th style=${th}>Board</th><th style=${th}>Status</th>
              <th style=${th}>When</th><th style=${th}>Clicks</th><th style=${th}>Impr</th><th style=${th}></th>
            </tr></thead>
            <tbody>
              ${site.recent.map(
                (p) => html`<tr title=${p.error ?? p.title}>
                  <td style=${td}>
                    <div
                      style="cursor:zoom-in"
                      @click=${() => openPinLightbox(p.id, client, onRefresh)}
                    >
                      ${pinThumb(p.id, client, onRefresh)}
                    </div>
                  </td>
                  <td style="${td};max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                    <a href=${p.link} target="_blank" rel="noopener" style="color:inherit">${p.title}</a>
                  </td>
                  <td style=${td}>${p.templateType}</td>
                  <td style="${td};font-size:12px">${p.boardName}</td>
                  <td style=${td}>${pinStatusChip(p.status)}</td>
                  <td style="${td};white-space:nowrap;font-size:12px;opacity:.8">
                    ${p.status === "pending" ? fmtEta(p.estimatedAt) : p.publishedAt ? fmtEta(p.publishedAt).slice(1) : "—"}
                  </td>
                  <td style=${td}>${num(p.clicks)}</td>
                  <td style=${td}>${num(p.impressions)}</td>
                  <td style="${td};white-space:nowrap">
                    ${
                      p.status === "failed"
                        ? html`<button
                          style="font-size:11px;padding:2px 10px;border-radius:999px;cursor:pointer"
                          @click=${() => retryPin(p.id)}
                        >
                          Retry
                        </button>`
                        : nothing
                    }
                    ${
                      p.status !== "published"
                        ? html`<button
                          title="Delete this pin — a fresh one (different template/angle) is designed in its place"
                          style="font-size:12px;padding:2px 9px;border-radius:999px;cursor:pointer;margin-left:4px"
                          @click=${() => replacePin(p.id)}
                        >
                          ✕
                        </button>`
                        : nothing
                    }
                  </td>
                </tr>`,
              )}
            </tbody>
          </table>
        </div>
      </div>

      ${renderStrategistCard(site)}
      ${renderPinLightbox(onRefresh)}
    </div>
  `;
}

// ─── Evolution sparkline (last 30 daily snapshots) ───────────────────────

function renderEvolution(
  daily: Array<{ date: string; impressions: number; clicks: number }>,
): TemplateResult {
  if (daily.length < 2) {
    return html`
      <div
        style="
          flex: 2;
          min-width: 220px;
          padding: 10px 14px;
          border-radius: 10px;
          background: rgba(128, 128, 128, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          opacity: 0.55;
        "
      >
        Evolution chart appears after 2+ days of analytics
      </div>
    `;
  }
  const w = 260;
  const h = 56;
  const maxI = Math.max(...daily.map((d) => d.impressions), 1);
  const maxC = Math.max(...daily.map((d) => d.clicks), 1);
  const x = (i: number) => (i / (daily.length - 1)) * (w - 8) + 4;
  const yI = (v: number) => h - 14 - (v / maxI) * (h - 22);
  const yC = (v: number) => h - 14 - (v / maxC) * (h - 22);
  const ptsI = daily.map((d, i) => `${x(i)},${yI(d.impressions)}`).join(" ");
  const ptsC = daily.map((d, i) => `${x(i)},${yC(d.clicks)}`).join(" ");
  return html`
    <div style="flex:2;min-width:220px;padding:8px 14px;border-radius:10px;background:rgba(128,128,128,.08)">
      <svg viewBox="0 0 ${w} ${h}" style="width:100%;height:${h}px;display:block">
        <polyline points=${ptsI} fill="none" stroke="#8B7355" stroke-width="1.6" opacity="0.75" />
        <polyline points=${ptsC} fill="none" stroke="#7C9A72" stroke-width="2" />
      </svg>
      <div style="font-size:10.5px;opacity:.6;display:flex;gap:12px">
        <span><span style="color:#7C9A72">■</span> clicks (max ${maxC})</span>
        <span><span style="color:#8B7355">■</span> impressions (max ${maxI})</span>
        <span style="margin-left:auto">${daily.length}d</span>
      </div>
    </div>
  `;
}

// ─── Pin lightbox (click thumbnail → full-size preview) ──────────────────

let _lightboxPinId: string | null = null;
let _lightboxImg: string | null = null;

function openPinLightbox(
  pinId: string,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
): void {
  _lightboxPinId = pinId;
  _lightboxImg = null;
  onRefresh();
  if (client) {
    void client
      .request<{ thumb: string }>("contentforge.pins.thumbnail", { pinId, size: 1000 })
      .then((res) => {
        if (res?.thumb && _lightboxPinId === pinId) {
          _lightboxImg = res.thumb;
          onRefresh();
        }
      })
      .catch(() => {});
  }
}

function renderPinLightbox(onRefresh: () => void): TemplateResult {
  if (!_lightboxPinId) {
    return html``;
  }
  const close = () => {
    _lightboxPinId = null;
    _lightboxImg = null;
    onRefresh();
  };
  return html`
    <div
      style="position:fixed;inset:0;z-index:9999;background:rgba(20,16,12,.78);display:flex;align-items:center;justify-content:center;cursor:zoom-out"
      @click=${close}
    >
      ${
        _lightboxImg
          ? html`<img
            src=${_lightboxImg}
            style="max-height:92vh;max-width:92vw;border-radius:14px;box-shadow:0 24px 80px rgba(0,0,0,.5)"
          />`
          : html`
              <div style="color: #fff; opacity: 0.8; font-size: 14px">Loading pin…</div>
            `
      }
    </div>
  `;
}

function renderStrategistCard(site: CfPinterestSite): TemplateResult {
  const s = site.strategist;
  if (!s) {
    return html``;
  }
  const collecting = s.measuredPins < s.minPinsForRetune;
  const kindIcon = (k: string) =>
    k === "pacing" ? "🚦" : k === "weights" ? "⚖️" : k === "keywords" ? "🌱" : "🧠";
  return html`
    <div style="margin-top:2px;padding:12px 14px;border-radius:10px;border:1px dashed rgba(128,128,128,.35)">
      <div style="display:flex;flex-wrap:wrap;align-items:baseline;gap:10px">
        <strong style="font-size:13.5px">🧠 PinStrategist — decisions &amp; learning</strong>
        <span style="font-size:12px;opacity:.75">
          measured <b>${s.measuredPins}</b>/${s.publishedPins} live pins
        </span>
      </div>
      ${
        s.byTemplate.length > 0
          ? html`<div style="font-size:12px;opacity:.75;margin-top:6px">
            ${s.byTemplate
              .map((t) => `${t.template}: ${t.clicks} clicks / ${t.saves} saves (${t.pins} pins)`)
              .join(" · ")}
          </div>`
          : nothing
      }
      ${
        (s.strategistLog ?? []).length > 0
          ? html`<div style="margin-top:8px;display:flex;flex-direction:column;gap:4px">
            ${(s.strategistLog ?? []).toReversed().map(
              (e) => html`<div style="font-size:12.5px;line-height:1.45">
                ${kindIcon(e.kind)}
                <span style="opacity:.55"
                  >${new Date(e.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span
                >
                ${e.detail}
              </div>`,
            )}
          </div>`
          : html`<div style="font-size:12px;opacity:.65;margin-top:6px">
            No decisions yet. ${
              collecting
                ? `Collecting data — analytics are pulled daily for pins older than 48h; the weekly reflection manages pacing (daily cap, pins/article), retunes template weights after ${s.minPinsForRetune}+ measured pins, and spawns new keyword ideas from winning topics.`
                : ""
            }
            Last analytics pull: ${fmtAgo(s.lastAnalyticsPullAt)} · last reflection: ${fmtAgo(s.lastReflectionAt)}.
          </div>`
      }
      ${
        s.lastReflectionReport
          ? html`<details style="margin-top:8px">
            <summary style="font-size:12px;opacity:.7;cursor:pointer">Last weekly report</summary>
            <pre
              style="margin:6px 0 0;padding:10px 12px;border-radius:8px;background:rgba(128,128,128,.08);font-size:12px;white-space:pre-wrap;font-family:inherit"
            >
${s.lastReflectionReport}</pre
            >
          </details>`
          : nothing
      }
    </div>
  `;
}

function renderLiveBar(data: ContentForgeDashboardData) {
  const workingAgents = data.agentStates.filter(
    (a: ContentForgeAgentState) => a.status === "working",
  );
  const counts = data.pipelineCounts;

  // Determine "Next Up" based on pipeline state
  let nextUp = "Pipeline idle";
  if ((counts as Record<string, number>).ideas > 0) {
    nextUp = `Strategist → outline for ${(counts as Record<string, number>).ideas} keyword${(counts as Record<string, number>).ideas > 1 ? "s" : ""}`;
  } else if ((counts as Record<string, number>).outline > 0) {
    nextUp = `Sourcer → products for ${(counts as Record<string, number>).outline} keyword${(counts as Record<string, number>).outline > 1 ? "s" : ""}`;
  } else if ((counts as Record<string, number>).sourcing > 0) {
    nextUp = `Writer → article for ${(counts as Record<string, number>).sourcing} keyword${(counts as Record<string, number>).sourcing > 1 ? "s" : ""}`;
  } else if ((counts as Record<string, number>).writing > 0) {
    nextUp = `Review → ${(counts as Record<string, number>).writing} article${(counts as Record<string, number>).writing > 1 ? "s" : ""} in review`;
  } else if ((counts as Record<string, number>).review > 0) {
    nextUp = `Publisher → ${(counts as Record<string, number>).review} article${(counts as Record<string, number>).review > 1 ? "s" : ""} to schedule`;
  }

  if (data.arthurPaused) {
    nextUp = "⏸ Arthur is paused — /resume to continue";
  }

  return html`
    <div class="cf-live-bar">
      <div class="cf-live-bar__section">
        <div class="cf-live-bar__title">⚡ Always Running</div>
        <div class="cf-live-bar__items">
          ${
            workingAgents.length === 0
              ? html`
                  <div class="cf-live-bar__idle">All agents idle</div>
                `
              : workingAgents.map((agent: ContentForgeAgentState) => {
                  const meta = AGENT_META[agent.type] ?? {
                    emoji: "🤖",
                    label: agent.type,
                    color: "#888",
                  };
                  return html`
                  <div class="cf-live-bar__item">
                    <span class="cf-live-bar__pulse"></span>
                    <span>${meta.emoji} ${meta.label}</span>
                    <span style="color: var(--cf-text-muted); font-size: 0.75rem;">
                      ${agent.currentTask ?? "working…"}
                    </span>
                  </div>
                `;
                })
          }
        </div>
      </div>
      <div class="cf-live-bar__section">
        <div class="cf-live-bar__title">📋 Next Up</div>
        <div class="cf-live-bar__items">
          <div class="cf-live-bar__item">
            <span style="font-size: 0.85rem;">${nextUp}</span>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderStatsBar(data: ContentForgeDashboardData) {
  const s = data.stats;
  return html`
    <div class="cf-stats">
      <div class="cf-stat cf-stat--accent">
        <div class="cf-stat__value">${s.publishedToday}</div>
        <div class="cf-stat__label">Published Today</div>
      </div>
      <div class="cf-stat cf-stat--success">
        <div class="cf-stat__value">${s.avgSeoScore || "—"}</div>
        <div class="cf-stat__label">Avg SEO Score</div>
      </div>
      <div class="cf-stat ${s.brokenLinks > 0 ? "cf-stat--danger" : ""}">
        <div class="cf-stat__value">${s.brokenLinks}</div>
        <div class="cf-stat__label">Broken Links</div>
      </div>
      <div class="cf-stat cf-stat--warning">
        <div class="cf-stat__value">${s.queueSize}</div>
        <div class="cf-stat__label">Queue Size</div>
      </div>
      <div class="cf-stat">
        <div class="cf-stat__value">${s.totalArticles}</div>
        <div class="cf-stat__label">Total Articles</div>
      </div>
      <div class="cf-stat">
        <div class="cf-stat__value">${s.totalProducts}</div>
        <div class="cf-stat__label">Total Products</div>
      </div>
    </div>
  `;
}

function renderPipeline(data: ContentForgeDashboardData) {
  const counts = data.pipelineCounts;
  return html`
    <div class="cf-section">
      <div class="cf-section__header">
        <div>
          <div class="cf-section__title">Content Pipeline</div>
          <div class="cf-section__subtitle">Keyword progression through the pipeline</div>
        </div>
      </div>
      <div class="cf-pipeline">
        ${PIPELINE_STAGES.map(
          (stage) => html`
            <div class="cf-pipeline__stage">
              <div class="cf-pipeline__count">${(counts as Record<string, number>)[stage.key] ?? 0}</div>
              <div class="cf-pipeline__label">${stage.label}</div>
            </div>
          `,
        )}
      </div>
    </div>
  `;
}

function renderAgentStatus(data: ContentForgeDashboardData) {
  return html`
    <div class="cf-section">
      <div class="cf-section__header">
        <div>
          <div class="cf-section__title">Agent Status</div>
          <div class="cf-section__subtitle">Team of 7 AI specialists</div>
        </div>
      </div>
      <div class="cf-agents-grid">
        ${data.agentStates.map((agent: ContentForgeAgentState) => {
          const meta = AGENT_META[agent.type] ?? {
            emoji: "🤖",
            label: agent.type,
            color: "var(--cf-text-secondary)",
          };
          return html`
            <div class="cf-agent-card">
              <div
                class="cf-agent-card__avatar"
                style="background: ${meta.color}22; color: ${meta.color};"
              >
                ${meta.emoji}
              </div>
              <div class="cf-agent-card__name">${meta.label}</div>
              <div class="cf-agent-card__role">${agent.type}</div>
              <div class="cf-agent-card__status">
                <span class="cf-agent-card__dot cf-agent-card__dot--${agent.status}"></span>
                ${
                  agent.status === "working" && agent.currentTask
                    ? agent.currentTask.slice(0, 30)
                    : agent.status
                }
              </div>
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

function renderActivityFeed(data: ContentForgeDashboardData, onRefresh: () => void) {
  return html`
    <div class="cf-section">
      <div class="cf-section__header">
        <div>
          <div class="cf-section__title">Recent Activity</div>
          <div class="cf-section__subtitle">Last 10 events</div>
        </div>
        <button class="cf-btn cf-btn--sm" @click=${onRefresh}>Refresh</button>
      </div>
      ${
        data.recentActivity.length === 0
          ? html`
              <div style="color: var(--cf-text-secondary); font-size: 13px; padding: 16px 0">
                No activity yet. Add a site and keywords to get started.
              </div>
            `
          : html`
            <ul class="cf-activity">
              ${data.recentActivity.map(
                (entry: ContentForgeActivityEntry) => html`
                  <li class="cf-activity__item">
                    <span class="cf-activity__badge ${statusColor(entry.status)}"></span>
                    <span class="cf-activity__time">${formatTime(entry.timestamp)}</span>
                    <span class="cf-activity__agent" style="color: ${AGENT_META[entry.agentType]?.color ?? "var(--cf-text-primary)"}">
                      ${AGENT_META[entry.agentType]?.label ?? entry.agentType}
                    </span>
                    <span class="cf-activity__text">${entry.details}</span>
                  </li>
                `,
              )}
            </ul>
          `
      }
    </div>
  `;
}

// ─── Empty State ────────────────────────────────────────────────────────

function renderEmptyState(props: ContentForgeProps) {
  return html`
    <div class="cf-empty">
      <div class="cf-empty__title">Welcome to Mission Control</div>
      <div class="cf-empty__text">
        Connect to your gateway to start managing your automated SEO content pipeline.
        The dashboard will show agent status, pipeline progress, and activity once connected.
      </div>
      <button class="cf-btn cf-btn--accent" style="margin-top: 16px;" @click=${props.onRefresh}>
        Load Dashboard
      </button>
    </div>
  `;
}

// ─── Placeholder Views ──────────────────────────────────────────────────

function renderPlaceholderView(subView: ContentForgeSubView) {
  const labels: Record<string, { title: string; desc: string }> = {
    tasks: { title: "Tasks Board", desc: "Kanban board for all work items — coming in Phase 3." },
    content: {
      title: "Content Pipeline",
      desc: "Full content pipeline from keyword to published — coming in Phase 3.",
    },
    approvals: {
      title: "Approvals Queue",
      desc: "HITL review queue for drafts, outlines, and product selections — coming in Phase 3b.",
    },
    calendar: {
      title: "Calendar",
      desc: "Scheduled tasks, cron jobs, and publish dates — coming in Phase 9.",
    },
    projects: {
      title: "Projects",
      desc: "Group keywords by campaign or project — coming in Phase 3b.",
    },
    memory: {
      title: "Memory",
      desc: "Searchable log of all agent memories and decisions — coming in Phase 11.",
    },
    office: {
      title: "Office",
      desc: "Pixel-art virtual office with live agent status — coming in Phase 11b.",
    },
    team: {
      title: "Team",
      desc: "Agent roster with pixelated avatars and roles — coming in Phase 11.",
    },
    settings: {
      title: "Settings",
      desc: "Site config, API keys, and rate limits — coming in Phase 12.",
    },
  };
  const info = labels[subView] ?? { title: subView, desc: "" };
  return html`
    <div class="cf-empty">
      <div class="cf-empty__title">${info.title}</div>
      <div class="cf-empty__text">${info.desc}</div>
    </div>
  `;
}
// ─── Team View ──────────────────────────────────────────────────────────

function renderTeamView(
  data: ContentForgeDashboardData | null,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
  activeSiteId?: string | null,
) {
  const arthur = AGENT_META.arthur;
  const workerAgents = [
    "strategist",
    "sourcer",
    "writer",
    "linker",
    "artist",
    "publisher",
    "auditor",
    "miner",
  ];

  function getAgentStatus(agentType: string) {
    return data?.agentStates?.find((a: ContentForgeAgentState) => a.type === agentType);
  }

  function renderAvatar(meta: typeof arthur, size: "sm" | "lg" = "sm") {
    const px = size === "lg" ? "64px" : "56px";
    if (meta.avatar) {
      return html`<img class="cf-team-card__avatar-img" src="${meta.avatar}" alt="${meta.label}" style="width: ${px}; height: ${px}; border-radius: 10px; object-fit: cover;" />`;
    }
    return html`<div class="cf-team-card__avatar" style="background: ${meta.color}22; color: ${meta.color}; width: ${px}; height: ${px};">${meta.emoji}</div>`;
  }

  return html`
    <div class="cf-team">
      <div class="cf-team__header">
        <div class="cf-section__title" style="font-size: 24px; text-align: center;">Meet the Team</div>
        <div class="cf-section__subtitle" style="text-align: center; margin-top: 4px;">9 AI agents, each with a real role and a real personality.</div>
      </div>

      <!-- Arthur Manager Card -->
      <div class="cf-team__manager">
        <div class="cf-team-card cf-team-card--manager cf-team-card--clickable"
          @click=${() => openAgentProfile("arthur", client, onRefresh, activeSiteId)}
          title="Click to edit Arthur's prompt">
          <div class="cf-team-card__top-row">
            ${renderAvatar(arthur, "lg")}
            <div>
              <div class="cf-team-card__name">${arthur.label}</div>
              <div class="cf-team-card__role">${arthur.role}</div>
            </div>
            <div class="cf-team-card__status-badge" style="margin-left: auto;">
              ${(() => {
                const state = getAgentStatus("arthur");
                const statusLabel = state?.status ?? "idle";
                return html`<span class="cf-agent-card__dot cf-agent-card__dot--${statusLabel}"></span> ${statusLabel}`;
              })()}
            </div>
          </div>
          <div class="cf-team-card__desc">${arthur.desc}</div>
          <div class="cf-team-card__skills">
            ${arthur.skills?.map((s) => html`<span class="cf-team-card__skill" style="background: ${arthur.color}22; color: ${arthur.color};">${s}</span>`)}
          </div>
          <div style="margin-top: 8px; display: flex; align-items: center; gap: 8px;">
            ${(() => {
              const isPaused = activeSiteId
                ? (data?.sitePauseStates?.[activeSiteId] ?? true)
                : (data?.arthurPaused ?? true);
              return html`
                <button
                  class="cf-btn cf-btn--sm ${isPaused ? "cf-btn--primary" : "cf-btn--ghost"}"
                  style="font-size: 12px;"
                  title="${isPaused ? "Start autonomous mode" : "Pause autonomous mode"}"
                  @click=${async (e: Event) => {
                    e.stopPropagation();
                    if (!client) {
                      return;
                    }
                    await client.request("contentforge.arthur.togglePause", {
                      siteId: activeSiteId ?? undefined,
                    });
                    onRefresh();
                  }}
                >
                  ${isPaused ? "▶ Resume" : "⏸ Pause"}
                </button>
                <span style="font-size: 11px; color: var(--cf-text-muted, #888);">
                  ${isPaused ? "⏸ Paused — manual only" : "▶ Autonomous mode"}
                </span>
              `;
            })()}
          </div>
        </div>
      </div>

      <!-- Connection Line -->
      <div class="cf-team__connector">
        <div class="cf-team__line"></div>
        <div class="cf-team__labels">
          <span>▾ INPUT SIGNAL</span>
          <span>OUTPUT ACTION ▸</span>
        </div>
      </div>

      <!-- Worker Agent Cards -->
      <div class="cf-team__grid">
        ${workerAgents.map((type) => {
          const meta = AGENT_META[type];
          if (!meta) {
            return nothing;
          }
          const state = getAgentStatus(type);
          const statusLabel = state?.status ?? "idle";
          const task =
            state?.status === "working" && state?.currentTask
              ? state.currentTask.slice(0, 30)
              : null;
          return html`
            <div class="cf-team-card cf-team-card--clickable"
              @click=${() => openAgentProfile(type, client, onRefresh, activeSiteId)}
              title="Click to edit ${meta.label}'s prompt">
              <div class="cf-team-card__top-row">
                ${renderAvatar(meta)}
                <div>
                  <div class="cf-team-card__name">${meta.label}</div>
                  <div class="cf-team-card__role">${meta.role ?? type}</div>
                </div>
                <div class="cf-team-card__status-badge" style="margin-left: auto;">
                  <span class="cf-agent-card__dot cf-agent-card__dot--${statusLabel}"></span>
                  ${task ?? statusLabel}
                </div>
              </div>
              <div class="cf-team-card__desc">${meta.desc ?? ""}</div>
              <div class="cf-team-card__skills">
                ${meta.skills?.map((s) => html`<span class="cf-team-card__skill" style="background: ${meta.color}22; color: ${meta.color};">${s}</span>`)}
              </div>
            </div>
          `;
        })}\
      </div>
      ${renderAgentProfileModal()}
    </div>
  `;
}

// ─── Agent Profile Modal ────────────────────────────────────────────────

function renderAgentProfileModal(): TemplateResult | typeof nothing {
  if (!_profileAgentKey) {
    return nothing;
  }
  const meta = AGENT_META[_profileAgentKey];
  if (!meta) {
    return nothing;
  }

  const avatarSize = "80px";
  const avatarEl = meta.avatar
    ? html`<img src="${meta.avatar}" alt="${meta.label}" style="width: ${avatarSize}; height: ${avatarSize}; border-radius: 12px; object-fit: cover;" />`
    : html`<div style="background: ${meta.color}22; color: ${meta.color}; width: ${avatarSize}; height: ${avatarSize}; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 32px;">${meta.emoji}</div>`;

  return html`
    <div class="cf-profile-backdrop" @click=${closeAgentProfile}></div>
    <div class="cf-profile-modal">
      <button class="cf-profile-modal__close" @click=${closeAgentProfile} title="Close">✕</button>

      <div class="cf-profile-modal__header">
        ${avatarEl}
        <div>
          <div class="cf-profile-modal__name">${meta.label}</div>
          <div class="cf-profile-modal__role">${meta.role ?? _profileAgentKey}</div>
        </div>
      </div>

      <div class="cf-profile-modal__desc">${meta.desc ?? ""}</div>

      <div class="cf-profile-modal__skills">
        ${meta.skills?.map((s) => html`<span class="cf-profile-modal__skill" style="background: ${meta.color}22; color: ${meta.color};">${s}</span>`)}
      </div>

      ${
        ["strategist", "writer", "artist", "arthur"].includes(_profileAgentKey ?? "")
          ? html`
      <div class="cf-profile-modal__prompt-section">
        <div class="cf-profile-modal__prompt-header">
          <label>🧠 System Prompt</label>
          <button class="cf-settings__btn cf-settings__btn--ghost" @click=${resetAgentPrompt} title="Reset to default">↻ Reset</button>
        </div>
        ${
          _profileLoading
            ? html`
                <div style="color: var(--cf-text-muted); padding: 16px">Loading prompt...</div>
              `
            : html`
            <textarea
              id="cf-profile-prompt"
              class="cf-settings__textarea"
              rows="12"
              placeholder="Enter custom prompt..."
              .value=${_profilePromptValue}
            ></textarea>
          `
        }
      </div>

      <div class="cf-profile-modal__actions">
        <button
          class="cf-settings__btn cf-settings__btn--secondary"
          @click=${closeAgentProfile}
        >Cancel</button>
        <button
          class="cf-settings__btn cf-settings__btn--primary"
          @click=${saveAgentPrompt}
          ?disabled=${_profileSaving || _profileLoading}
        >${_profileSaving ? "Saving..." : _profileSaved ? "✅ Saved!" : "💾 Save Prompt"}</button>
      </div>
      `
          : html`
      <div style="color: var(--cf-text-muted); padding: 16px; font-size: 0.85rem; text-align: center;">
        This agent uses built-in logic and cannot be customized via prompt.
      </div>
      <div class="cf-profile-modal__actions">
        <button
          class="cf-settings__btn cf-settings__btn--secondary"
          @click=${closeAgentProfile}
        >Close</button>
      </div>
      `
      }
    </div>
  `;
}
